// Where to go after signing in (TF-235). People type /login out of habit; signing in there used to come
// back to /login, which is no page, and showed "This page does not exist" as the first screen.
// (/auth is Keycloak's at the gateway, so the app never sees it.)

/** The addresses people type to sign in: each starts sign-in, or opens the start page once signed in. */
export const SIGN_IN_PATHS = ['/login', '/signin', '/sign-in'] as const;
export const SIGN_OUT_PATH = '/logout';

const NOT_A_DESTINATION = new Set<string>([...SIGN_IN_PATHS, SIGN_OUT_PATH, '/auth-callback']);

/**
 * The address to return to, cleaned: a path inside the app (never another site), not a sign-in or
 * sign-out address, and one the app has a page for when `isPage` can say. Anything else is the start page.
 */
export function cleanReturnTo(raw: string | null | undefined, isPage?: (path: string) => boolean): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/';
  const path = raw.split(/[?#]/, 1)[0]!.replace(/\/+$/, '') || '/';
  if (NOT_A_DESTINATION.has(path.toLowerCase())) return '/';
  if (isPage && !isPage(path)) return '/';
  return raw;
}

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The words in an address the app has no page for, to search with: /c/<id>/dealz -> "dealz". */
export function wordsOfPath(path: string): string {
  return path
    .split('/')
    .filter((part, index, parts) => part && !ID.test(part) && !(part === 'c' && index === 1 && parts.length > 2))
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    })
    .join(' ')
    .replace(/[-_+.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100);
}
